/**
 * Main orchestration for the free fall simulation
 * Connects simulation, rendering, and UI layers
 */

import { Simulation } from './simulation/physics';
import { Renderer } from './rendering/renderer';
import { Controls } from './ui/controls';
import type { SimulationParams } from './types';

class FreeFallApp {
  private simulation: Simulation;
  private renderer: Renderer;
  private controls: Controls;
  private statusMessage: HTMLElement;
  private animationId: number | null = null;
  private lastTime: number = 0;
  private animationSpeed: number = 1.0;
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private isComplete: boolean = false;

  constructor() {
    // Initialize simulation with default parameters
    const params: SimulationParams = {
      leftTowerX: -16,
      rightTowerX: 16,
      towerHeight: 20,
      leftLaunch: {
        velocity: 20,       // m/s
        angle: 25,          // degrees
      },
      rightLaunch: {
        velocity: 0,        // m/s (free fall)
        angle: -90,         // degrees (vertical down)
      },
      gravity: 9.81,        // m/s²
      groundLevel: 0,       // meters
    };

    this.simulation = new Simulation(params);

    // Get canvas and initialize renderer
    const canvas = document.querySelector<HTMLCanvasElement>('#viewport');
    if (!canvas) throw new Error('Canvas not found');
    this.renderer = new Renderer(canvas);

    // Listen for canvas resize events to trigger re-render
    canvas.addEventListener('canvasResized', () => {
      if (!this.isRunning) {
        this.render();
      }
    });

    // Get status message element
    const statusElement = document.querySelector<HTMLElement>('#statusMessage');
    if (!statusElement) throw new Error('Status message element not found');
    this.statusMessage = statusElement;

    // Initialize controls with callbacks
    this.controls = new Controls({
      onPlay: () => this.handlePlay(),
      onReset: () => this.handleReset(),
      onInitial: () => this.handleInitial(),
      onLeftVelocityChange: (value) => this.updateLeftVelocity(value),
      onLeftAngleChange: (value) => this.updateLeftAngle(value),
      onAnimationSpeedChange: (value) => this.updateAnimationSpeed(value),
      onShowGuidesChange: (show) => this.renderer.setShowGuides(show),
    });

    // Set initial guide state and animation speed
    this.renderer.setShowGuides(this.controls.getShowGuides());
    this.animationSpeed = this.controls.getAnimationSpeed();

    // Initial render with resize - ensure it happens after layout
    // Use setTimeout to ensure CSS has been applied and layout is complete
    setTimeout(() => {
      const params2 = this.simulation.getParams();
      this.renderer.resize(params2);
      this.render();
    }, 0);
  }

  private handlePlay(): void {
    if (this.isComplete) {
      // Auto-reset when complete and play is clicked
      this.handleReset();
      this.startSimulation();
    } else if (this.isPaused) {
      // Resume from pause
      this.isPaused = false;
      this.isRunning = true;
      this.controls.setPlayButtonToPause();
      this.startAnimation();
    } else if (this.isRunning) {
      // Pause the running simulation
      this.isPaused = true;
      this.isRunning = false;
      this.stopAnimation();
      this.controls.setPlayButtonToResume();
    } else {
      // Start new simulation
      this.startSimulation();
    }
  }

  private startSimulation(): void {
    this.simulation.launch();
    this.hideStatusMessage();
    this.isRunning = true;
    this.isPaused = false;
    this.isComplete = false;
    this.controls.setPlayButtonToPause();
    this.startAnimation();
  }

  private handleReset(): void {
    this.simulation.reset();
    this.isRunning = false;
    this.isPaused = false;
    this.isComplete = false;
    this.stopAnimation();
    this.hideStatusMessage();
    this.controls.setPlayButtonToPlay();

    // Recalculate canvas height and render
    const params = this.simulation.getParams();
    this.renderer.resize(params);
    this.render();
  }

  private handleInitial(): void {
    this.controls.resetToInitialValues();
    // Trigger updates for velocity and angle
    this.updateLeftVelocity(this.controls.getLeftVelocity());
    this.updateLeftAngle(this.controls.getLeftAngle());
    this.updateAnimationSpeed(this.controls.getAnimationSpeed());
    this.handleReset();
  }

  private updateLeftVelocity(value: number): void {
    const params = this.simulation.getParams();
    params.leftLaunch.velocity = value;
    this.simulation.updateParams(params);
  }

  private updateLeftAngle(value: number): void {
    const params = this.simulation.getParams();
    params.leftLaunch.angle = value;
    this.simulation.updateParams(params);
    this.render(); // Re-render to show launcher angle
  }

  private updateAnimationSpeed(value: number): void {
    this.animationSpeed = value;
  }

  private startAnimation(): void {
    if (this.animationId !== null) return;

    this.lastTime = performance.now();
    const animate = (currentTime: number) => {
      const dt = Math.min((currentTime - this.lastTime) / 1000, 0.05) * this.animationSpeed;
      this.lastTime = currentTime;

      this.simulation.step(dt);
      this.render();

      const state = this.simulation.getState();

      if (state.hasCollided) {
        this.stopAnimation();
        this.isRunning = false;
        this.isComplete = true;
        this.controls.setPlayButtonToPlay();
      } else if (state.allBallsLanded) {
        this.stopAnimation();
        this.isRunning = false;
        this.isComplete = true;
        this.controls.setPlayButtonToPlay();
      } else if (state.isRunning && this.isRunning) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.stopAnimation();
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  private stopAnimation(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private render(): void {
    const state = this.simulation.getState();
    const params = this.simulation.getParams();
    this.renderer.render(state, params);
  }

  // private showStatusMessage(message: string, type: 'success' | 'danger' | 'warning'): void {
  //   this.statusMessage.textContent = message;
  //   this.statusMessage.className = `status status--${type}`;
  //   this.statusMessage.style.display = 'block';
  // }

  private hideStatusMessage(): void {
    this.statusMessage.style.display = 'none';
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FreeFallApp());
} else {
  new FreeFallApp();
}
